import type { User } from './types'

export interface KeycloakConfig {
  url: string
  realm: string
  clientId: string
  tenantCode: string | null
}

const DEFAULT_KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8080'
const DEFAULT_KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM || 'spectreiq'
const DEFAULT_KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'spectreiq-ui'
const DEFAULT_TENANT_CODE = normalizeTenantCode(import.meta.env.VITE_TENANT_CODE || null)
const TENANT_QUERY_KEYS = ['tenant', 'tenant_code']

export function normalizeTenantCode(value: string | null | undefined): string | null {
  const normalized = (value || '').trim().toUpperCase()
  return normalized || null
}

export function parseTenantRealmMap(raw: string | null | undefined): Record<string, string> {
  if (!raw) {
    return {}
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    return Object.entries(parsed).reduce<Record<string, string>>((map, [tenantCode, realmName]) => {
      const normalizedTenantCode = normalizeTenantCode(tenantCode)
      const normalizedRealmName = typeof realmName === 'string' ? realmName.trim() : ''
      if (!normalizedTenantCode || !normalizedRealmName) {
        return map
      }
      map[normalizedTenantCode] = normalizedRealmName
      return map
    }, {})
  } catch (error) {
    console.warn('[Auth] Failed to parse tenant realm map', error)
    return {}
  }
}

export const tenantRealmMap = parseTenantRealmMap(import.meta.env.VITE_TENANT_REALM_MAP || null)

export function resolveTenantCodeFromSearch(
  search: string,
  fallbackTenantCode: string | null = DEFAULT_TENANT_CODE
): string | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  for (const key of TENANT_QUERY_KEYS) {
    const tenantCode = normalizeTenantCode(params.get(key))
    if (tenantCode) {
      return tenantCode
    }
  }
  return fallbackTenantCode
}

export function hasTenantRealm(tenantCode: string | null | undefined): boolean {
  const normalizedTenantCode = normalizeTenantCode(tenantCode)
  return !!(normalizedTenantCode && tenantRealmMap[normalizedTenantCode])
}

export function inferRealmFromIssuer(issuer: string | null | undefined): string | null {
  if (!issuer) {
    return null
  }

  const match = issuer.match(/\/realms\/([^/]+)\/?$/)
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

export function resolveKeycloakConfig(options: {
  tenantCode?: string | null
  realm?: string | null
} = {}): KeycloakConfig {
  const tenantCode = normalizeTenantCode(options.tenantCode ?? DEFAULT_TENANT_CODE)
  const realm =
    (options.realm || (tenantCode ? tenantRealmMap[tenantCode] : null) || DEFAULT_KEYCLOAK_REALM).trim()

  return {
    url: DEFAULT_KEYCLOAK_URL,
    realm,
    clientId: DEFAULT_KEYCLOAK_CLIENT_ID,
    tenantCode,
  }
}

export function resolveKeycloakConfigFromSearch(search: string): KeycloakConfig {
  return resolveKeycloakConfig({
    tenantCode: resolveTenantCodeFromSearch(search),
  })
}

export const keycloakConfig = resolveKeycloakConfig()

export const REDIRECT_URI =
  typeof window !== 'undefined' ? `${window.location.origin}/login` : 'http://localhost:5173/login'

export const DEV_MODE = import.meta.env.DEV && import.meta.env.VITE_AUTH_MODE === 'disabled'

export const devUser: User | null = import.meta.env.DEV
  ? {
      id: 'dev-user',
      username: 'developer',
      email: 'dev@spectreiq.local',
      firstName: 'Dev',
      lastName: 'User',
      roles: ['admin', 'analyst'],
      tenantCode: DEFAULT_TENANT_CODE || undefined,
    }
  : null
